
import {Options, Vue} from 'vue-class-component';
import AppointmentService from "@/services/AppointmentService";
import CourseService from "@/services/CourseService";
import EventBusService from "@/services/EventBusService";
import UserService from "@/services/UserService";
import {RecordIdentifier} from "@/data/global";
import {Course, CourseEnrollment, CourseGroup} from "@/data/course";
import UserCourseUpcomingAppointment from "@/components/UserCourseUpcomingAppointment.vue";
import UserCourseEnrollment from "@/components/UserCourseEnrollment.vue";
import {AppointmentEnrollment, AppointmentEnrollmentState, AppointmentEnrollmentStatesMap} from "@/data/appointments";
import {AppointmentEnrollmentEvent, LoadAllCourseAppointmentsEvent} from "@/data/eventbus";

@Options({
    props: {},
    components: {
        UserCourseEnrollment,
        UserCourseUpcomingAppointment,
    },
})
export default class UserCourses extends Vue {
    // regionId!: number

    manualCourseOrder = [
        'Fit durch die Schwangerschaft',
        'buggyFit meets Moms to be',
        'Beckenbodenworkout',
        'buggyFit',
        'Rückbildungsmix',
        'Rückbildung Videokurs',
        'Walken nach der Schwangerschaft',
        'Back to Running',
        'SportyMum only4you',
        'evening fit',
        'fit & play',
        'Special',
        'mummyFit',
        'Online Live Kurse',
        'testfgdgfddf',
        'Test Test',
    ];

    loading = true
    loadingEnrollmentStates = true

    courseEnrollments: CourseEnrollment[] = []
    appointmentEnrollmentStates: AppointmentEnrollmentStatesMap = {}

    compileAppointmentIds() {
        return this.courseEnrollments.reduce<RecordIdentifier[]>((ids, courseEnrollment) => {
            return ids.concat(courseEnrollment.course?.appointments_upcoming?.map(appointment => appointment.id) || []);
        }, []);
    }

    loadData() {
        this.loading = true;
        return UserService.getEnrollments().then(result => {
            this.courseEnrollments = result.data.courses;
            console.log(this.courseEnrollments)
        }).finally(() => this.loading = false);
    }

    loadAllCourseAppointments(event: LoadAllCourseAppointmentsEvent) {
        const course = this.courseEnrollments.find(enrollment => enrollment.course_id === event.course)?.course;

        if (!course) {
            console.error('loadAllCourseAppointments: could not find given course id');
            return;
        }

        EventBusService.$emit('user.courses.appointments.loadAll:loading', event);
        CourseService.getUserCourseAppointments(event.course).then(result => {
            const appointments = result.data.data;
            course.appointments_upcoming = [...appointments];

            this.loadEnrollmentStates();
        }).finally(() => EventBusService.$emit('user.courses.appointments.loadAll:finished', event));
    }

    loadEnrollmentStates(highlightAppointmentId?: RecordIdentifier) {
        this.loadingEnrollmentStates = true;

        const ids = this.compileAppointmentIds();

        if (typeof highlightAppointmentId !== 'undefined' && !ids.includes(highlightAppointmentId)) {
            return; // event was fired for an appointment that we do not have in this course, so do not update enrollment states from server
        }

        this.appointmentEnrollmentStates = {
            ...ids.reduce<any/*AppointmentEnrollmentState*/>((map, id: RecordIdentifier) => ({
                ...map,
                [id]: {enrollment: null, loading: true}
            }), {}),
            ...this.appointmentEnrollmentStates // existing state does not get loading flag (UX)
        };

        return AppointmentService.getAppointmentEnrollmentBatchStatus(this.compileAppointmentIds()).then(result => {
            const enrollments = result.data.enrollments as Record<RecordIdentifier, AppointmentEnrollment>;
            const enrollmentStates: AppointmentEnrollmentStatesMap = {};

            Object.keys(enrollments).forEach(key => {
                enrollmentStates[key as unknown as RecordIdentifier] = {
                    enrollment: enrollments[key as unknown as RecordIdentifier],
                    loading: false,
                };
            });

            Object.values(this.appointmentEnrollmentStates).forEach(state => {
                state.loading = false;
            });

            this.appointmentEnrollmentStates = {
                ...this.appointmentEnrollmentStates,
                ...enrollmentStates
            };
        }).finally(() => this.loadingEnrollmentStates = false);
    }

    onAppointmentEnrollmentChanged(event: AppointmentEnrollmentEvent) {
        this.loadEnrollmentStates(event.appointment);
    }

    onCourseEnrollmentChanged() {
        this.loadData();
    }

    get sortedCourseGroups(): (CourseGroup | null)[] {
        const uniqueGroups = new Map<number | null, CourseGroup | null>();

        this.courseEnrollments.forEach(enrollment => {
            if (enrollment.course_group) {
                uniqueGroups.set(enrollment.course_group.id, enrollment.course_group);
            } else {
                uniqueGroups.set(null, null); // Explicitly add null for "Sonstige Kurse"
            }
        });

        return Array.from(uniqueGroups.values()).sort((a, b) => {
            if (!a) return 1;  // null groups go last
            if (!b) return -1;  // null groups go last
            return a.sort_number - b.sort_number;
        });
    }

    getCoursesForGroup(group: CourseGroup | null): CourseEnrollment[] {
        return this.courseEnrollments.filter(enrollment => {
            const courseGroup = enrollment.course_group;
            if (!group) {
                return !courseGroup;
            }
            return courseGroup?.id === group.id;
        });
    }

    mounted() {
        this.loadData().then(() => this.loadEnrollmentStates());

        EventBusService.$on('user.appointments.enrollment:changed', this.onAppointmentEnrollmentChanged);
        EventBusService.$on('user.courses.appointments.loadAll', this.loadAllCourseAppointments);
        EventBusService.$on('user.courses.enrollment:changed', this.onCourseEnrollmentChanged);
    }

    beforeUnmount() {
        EventBusService.$off('user.appointments.enrollment:changed', this.onAppointmentEnrollmentChanged);
        EventBusService.$off('user.courses.appointments.loadAll', this.loadAllCourseAppointments);
        EventBusService.$off('user.courses.enrollment:changed', this.onCourseEnrollmentChanged);
    }
}
